import { useMutation, useQuery, useQueryClient } from 'react-query';
import { useAccessToken } from 'utils/auth';
import { availabilityApi } from './client';
import {
  USE_AVAILABILITY_EXCEPTIONS_QUERY_KEY,
  USE_AVAILABILITY_QUERY_KEY,
  USE_SEMESTER_BREAKS_QUERY_KEY
} from './constants';
import {
  CreateHoursRequestBody,
  UpdateAvailabilityBody,
  UpsertAvailabilityExceptionBody,
  UpsertSemesterBreakBody
} from './types';

export const useAvailabilityQuery = () => {
  const { token } = useAccessToken();

  return useQuery(
    [USE_AVAILABILITY_QUERY_KEY, token],
    () => availabilityApi.fetchAvailability(token),
    { enabled: !!token }
  );
};

export const useAvailabilityExceptionsQuery = () => {
  const { token } = useAccessToken();

  return useQuery(
    [USE_AVAILABILITY_EXCEPTIONS_QUERY_KEY, token],
    () => availabilityApi.fetchAvailabilityExceptions(token),
    { enabled: !!token }
  );
};

export const useSemesterBreaksQuery = (enabled: boolean) => {
  const { token } = useAccessToken();

  return useQuery(
    [USE_SEMESTER_BREAKS_QUERY_KEY, token],
    () => availabilityApi.fetchSemesterBreaks(token),
    { enabled: !!token && enabled }
  );
};

export const useUpdateAvailabilityMutation = () => {
  const { token } = useAccessToken();
  const queryClient = useQueryClient();

  return useMutation(
    (body: UpdateAvailabilityBody) =>
      availabilityApi.updateAvailability(token, body),
    {
      onSuccess: () => {
        void queryClient.invalidateQueries([USE_AVAILABILITY_QUERY_KEY]);
      }
    }
  );
};

export const useUpsertExceptionMutation = () => {
  const { token } = useAccessToken();
  const queryClient = useQueryClient();

  return useMutation(
    ({
      year,
      calendarWeek,
      body
    }: {
      year: number;
      calendarWeek: number;
      body: UpsertAvailabilityExceptionBody;
    }) =>
      availabilityApi.upsertAvailabilityException(
        token,
        year,
        calendarWeek,
        body
      ),
    {
      onSuccess: () => {
        void queryClient.invalidateQueries([
          USE_AVAILABILITY_EXCEPTIONS_QUERY_KEY
        ]);
      }
    }
  );
};

export const useDeleteExceptionMutation = () => {
  const { token } = useAccessToken();
  const queryClient = useQueryClient();

  return useMutation(
    (refId: string) =>
      availabilityApi.deleteAvailabilityException(token, refId),
    {
      onSuccess: () => {
        void queryClient.invalidateQueries([
          USE_AVAILABILITY_EXCEPTIONS_QUERY_KEY
        ]);
      }
    }
  );
};

export const useCreateSemesterBreakMutation = () => {
  const { token } = useAccessToken();
  const queryClient = useQueryClient();

  return useMutation(
    (body: UpsertSemesterBreakBody) =>
      availabilityApi.createSemesterBreak(token, body),
    {
      onSuccess: () => {
        void queryClient.invalidateQueries([USE_SEMESTER_BREAKS_QUERY_KEY]);
      }
    }
  );
};

export const useUpdateSemesterBreakMutation = () => {
  const { token } = useAccessToken();
  const queryClient = useQueryClient();

  return useMutation(
    ({ refId, body }: { refId: string; body: UpsertSemesterBreakBody }) =>
      availabilityApi.updateSemesterBreak(token, refId, body),
    {
      onSuccess: () => {
        void queryClient.invalidateQueries([USE_SEMESTER_BREAKS_QUERY_KEY]);
      }
    }
  );
};

export const useDeleteSemesterBreakMutation = () => {
  const { token } = useAccessToken();
  const queryClient = useQueryClient();

  return useMutation(
    (refId: string) => availabilityApi.deleteSemesterBreak(token, refId),
    {
      onSuccess: () => {
        void queryClient.invalidateQueries([USE_SEMESTER_BREAKS_QUERY_KEY]);
      }
    }
  );
};

export const useCreateHoursRequestMutation = () => {
  const { token } = useAccessToken();
  const queryClient = useQueryClient();

  return useMutation(
    ({
      breakRefId,
      body
    }: {
      breakRefId: string;
      body: CreateHoursRequestBody;
    }) => availabilityApi.createHoursRequest(token, breakRefId, body),
    {
      onSuccess: () => {
        void queryClient.invalidateQueries([USE_SEMESTER_BREAKS_QUERY_KEY]);
      }
    }
  );
};

export const useCancelHoursRequestMutation = () => {
  const { token } = useAccessToken();
  const queryClient = useQueryClient();

  return useMutation(
    ({
      breakRefId,
      requestRefId
    }: {
      breakRefId: string;
      requestRefId: string;
    }) => availabilityApi.cancelHoursRequest(token, breakRefId, requestRefId),
    {
      onSuccess: () => {
        void queryClient.invalidateQueries([USE_SEMESTER_BREAKS_QUERY_KEY]);
      }
    }
  );
};
